import React, { ChangeEvent, useState } from 'react';

/* Components */
import PreQuestionInput from './PreQuestionInput';
import PreQuestionTextArea from './PreQuestionTextArea';
import PreQuestionDropdown from './PreQuestionDropdown';
import PreQuestionCheckbox from './PreQuestionCheckbox';
import QuestionDevices from './QuestionDevices';

/* Store */
import useAddSkuStore from '@scenes/AddSkuPage/store/store.addsku';

/* Types */
import { AddSkuSelectedSku, Question, Sku } from 'types/sku.types';
import { AddSkuQuantityChangeProps } from '@scenes/AddSkuPage/store/store.types';
import { ErrorHashProp, QuestionTypes } from 'types/qa.types';
import { isStringType } from './utils';

type PreQuestionProps = {
  question: Question;
  item: AddSkuSelectedSku;
  sku: Sku;
  error: string;
};

const PreQuestion = (props: PreQuestionProps) => {
  /* Store Actions */
  const { updateAddSkuAnswer, updateAddSkuQuantity, updateAddSkuDeviceInputAnswer, updateAddSkuDeviceAnswer } = useAddSkuStore(
    state => state.actions,
  );

  const { question, error, sku, item } = props;
  const [makeInputValue, setMakeInputValue] = useState('');

  const inputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    event.preventDefault();
    updateAddSkuAnswer({ question, value: event.target.value });
  };

  const inputValue = () => item?.questions?.[question.id]?.text;

  const dropdownChange = () => (value: string) => {
    updateAddSkuAnswer({ question, value });
  };

  const dropdownValue = () => item?.questions?.[question.id];

  const checkboxChange = (value: string) => {
    updateAddSkuAnswer({
      question,
      value,
    });
  };

  const checkboxQuantityChange =
    () =>
    ({ id, quantity }: AddSkuQuantityChangeProps) => {
      updateAddSkuQuantity({
        question,
        id,
        quantity,
      });
    };

  const checkboxValue = () => item?.questions?.[question.id];

  /*
  ----------------------------------------------
    START DEVICE CATALOGING onchange & values
  ----------------------------------------------
  */
  const questionRef = item.questions[question.id];
  const makeValue = questionRef?.answerValue;
  const modelValue = questionRef?.model;
  const isMakeValueString = isStringType(makeValue);
  const isModelValueString = isStringType(modelValue);

  const makeDropdownChange = (value: number) => {
    /*
      If any change to make dropdown is made,
      reset the local state makeInputValue to ''
    */
    setMakeInputValue('');
    updateAddSkuDeviceAnswer({
      question,
      questionType: 'makeDropdown',
      makeValue: value,
      modelValue: null,
    });
  };

  const modelDropdownChange = (value: number) =>
    updateAddSkuDeviceAnswer({
      questionType: 'modelDropdown',
      question,
      makeValue,
      modelValue: value,
    });

  const makeInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    setMakeInputValue(event.target.value);
    updateAddSkuDeviceInputAnswer({
      questionType: 'makeInput',
      question,
      makeInputValue: event.target.value,
      modelInputValue: modelValue,
    });
  };

  const modelInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    event.preventDefault();
    /*
      When on edit, check if make input is present.
      If yes, it will send makeInputValue to PreQuestionDevice component which renders inputfield
      If not, render dropdown with make value.
    */
    if (makeInputValue) {
      updateAddSkuDeviceInputAnswer({
        questionType: 'makeInput',
        question,
        makeInputValue,
        modelInputValue: event.target.value,
      });
    } else {
      updateAddSkuDeviceInputAnswer({
        questionType: 'modelInput',
        question,
        makeInputValue: makeValue,
        modelInputValue: event.target.value,
      });
    }
  };

  const resetDeviceQuestion = () => {
    /*
      This function triggers when X icon on make input is clicked.
      It resets all values to null.
    */
    updateAddSkuDeviceAnswer({
      questionType: 'makeDropdown',
      question,
      makeValue: null,
      modelValue: null,
    });
  };
  /*
  ----------------------------------------------
    END DEVICE CATALOGING onchange & values
  ----------------------------------------------
  */
  return (
    <div id={question.id as string}>
      {(() => {
        switch (question.inputType) {
          case QuestionTypes.Input:
            return (
              <PreQuestionInput
                question={question}
                error={error}
                onChange={inputChange}
                value={item?.questions?.[question.id]?.text}
                key={`input${question.id}`}
              />
            );

          case QuestionTypes.Textarea:
            return <PreQuestionTextArea question={question} error={error} onChange={inputChange} value={inputValue()} />;

          case QuestionTypes.Dropdown:
            return <PreQuestionDropdown question={question} error={error} sku={sku} onChange={dropdownChange()} value={dropdownValue()} />;

          case QuestionTypes.Checkbox:
            return (
              <PreQuestionCheckbox
                question={question}
                error={error}
                sku={sku}
                onChange={checkboxChange}
                onQuantityChange={checkboxQuantityChange()}
                value={checkboxValue()}
              />
            );

          case QuestionTypes.Device:
            return (
              <QuestionDevices
                question={question}
                error={error as unknown as ErrorHashProp}
                sku={sku}
                value={makeValue} // MAKE VALUE
                modelValue={modelValue} // MODEL VALUE
                makeInputValue={isMakeValueString ? makeValue : makeInputValue} // MAKE INPUT VALUE
                modelInputValue={isModelValueString ? modelValue : ''} // MODEL INPUT VALUE
                onMakeDropdownChange={makeDropdownChange} // MAKE ONCHANGE
                onModelDropdownChange={modelDropdownChange} // MODEL ONCHANGE
                onMakeInputChange={makeInputChange} // MAKE INPUT ONCHANGE
                onModelInputChange={modelInputChange} // MODEL INPUT ONCHANGE
                onIconClick={resetDeviceQuestion}
              />
            );

          default:
            return null;
        }
      })()}
    </div>
  );
};

export default PreQuestion;
